import React, { useState, useEffect, useCallback } from 'react';
import {
  LogOut, Menu, X, BarChart3, ShoppingBag, Users,
  Eye, EyeOff, Lock, Mail, LayoutGrid, Settings,
  ArrowRight, Bell, AlertCircle, Loader2, LogIn, User,
  Send, CheckCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';

const colorMap = {
  secondary: {
    base: '#f97316',
    hover: '#ea580c',
    light: '#fdba74',
    dark: '#c2410c',
    opacity10: 'rgba(249, 115, 22, 0.1)',
  },
  primary: {
    base: '#22c55e',
    hover: '#16a34a',
    light: '#86efac',
    opacity10: 'rgba(34, 197, 94, 0.1)',
  },
  accent: {
    base: '#3b82f6',
    hover: '#2563eb',
    light: '#93c5fd',
  },
  text: {
    primary: '#111827',
    secondary: '#6b7280',
    muted: '#9ca3af',
    dark: '#374151',
  },
  bg: {
    white: '#ffffff',
    light: '#f9fafb',
    card: '#ffffff',
    disabled: '#d1d5db',
  },
  border: {
    default: '#e5e7eb',
  },
  status: {
    error: '#ef4444',
    success: '#22c55e',
    warning: '#f97316',
    info: '#3b82f6',
  },
};

// API_BASE_URL n'est plus utilisé pour l'admin (tout passe par Supabase)

// Hook pour Realtime notifications
function useNotificationWebSocket(token) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected] = useState(true);

  useEffect(() => {
    if (!token) return;
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) return;
    const admin = JSON.parse(adminUser);

    const loadInitialNotifications = async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', admin.id)
        .order('created_at', { ascending: false })
        .limit(50);
      if (!error) {
        setNotifications(data || []);
        setUnreadCount(data.filter(n => !n.is_viewed).length);
      }
    };

    loadInitialNotifications();

    const channel = supabase
      .channel('admin_notifications_realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${admin.id}`,
        },
        (payload) => {
          const newNotif = payload.new;
          setNotifications(prev => [newNotif, ...prev]);
          setUnreadCount(prev => prev + 1);

          if (Notification.permission === 'granted') {
            new Notification(newNotif.title, {
              body: newNotif.message,
              icon: '/logo-image.png',
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [token]);

  return { notifications, unreadCount, isConnected, setNotifications, setUnreadCount };
}

export default function AdminPanel() {
  const [authState, setAuthState] = useState('login');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [token, setToken] = useState(null);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    setMounted(true);
    const savedToken = localStorage.getItem('adminToken');
    const savedAdmin = localStorage.getItem('adminUser');
    
    if (savedToken && savedAdmin) {
      setToken(savedToken);
      setAdmin(JSON.parse(savedAdmin));
      setIsLoggedIn(true);
      setAuthState('dashboard');
    }
  }, []);

  if (!mounted) return null;

  if (!isLoggedIn) {
    return authState === 'login' ? 
      <AdminLogin 
        setAuthState={setAuthState}
        setIsLoggedIn={setIsLoggedIn}
        setToken={setToken}
        setAdmin={setAdmin}
      /> :
      <AdminRegister 
        setAuthState={setAuthState}
        setIsLoggedIn={setIsLoggedIn}
        setToken={setToken}
        setAdmin={setAdmin}
      />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colorMap.bg.light }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        
        * {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        h1, h2, h3 {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        
        .animate-slideInUp {
          animation: slideInUp 0.5s ease-out;
        }
        
        .animate-slideInDown {
          animation: slideInDown 0.5s ease-out;
        }

        .animate-slideInRight {
          animation: slideInRight 0.5s ease-out;
        }

        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }

        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }

        .glass-effect {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(249, 115, 22, 0.1);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${colorMap.secondary.light};
          border-radius: 3px;
        }
      `}</style>

      <div className="flex h-screen overflow-hidden">
        <Sidebar 
          isOpen={sidebarOpen} 
          setIsOpen={setSidebarOpen}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          admin={admin}
          setIsLoggedIn={setIsLoggedIn}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar 
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            admin={admin}
          />

          <div className="flex-1 overflow-auto custom-scrollbar">
            <div className="p-6 lg:p-8">
              {currentPage === 'dashboard' && <DashboardPage token={token} />}
              {currentPage === 'products' && <ProductsPage token={token} />}
              {currentPage === 'sellers' && <SellersPage token={token} />}
              {currentPage === 'orders' && <OrdersPage token={token} />}
              {currentPage === 'analytics' && <AnalyticsPage token={token} />}
              {currentPage === 'notifications' && <NotificationsPage token={token} />}
              {currentPage === 'settings' && <SettingsPage admin={admin} token={token} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* LOGIN */
function AdminLogin({ setAuthState, setIsLoggedIn, setToken, setAdmin }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('login_admin', {
        p_email: formData.email,
        p_password: formData.password,
      });

      if (rpcError) throw rpcError;
      if (!data.success) {
        throw new Error(data.error || 'Email ou mot de passe incorrect');
      }

      const mockToken = 'supabase_admin_session_' + data.admin.id;

      localStorage.setItem('adminToken', mockToken);
      localStorage.setItem('adminUser', JSON.stringify(data.admin));
      
      setToken(mockToken);
      setAdmin(data.admin);
      setIsLoggedIn(true);;

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const canLogin = formData.email && formData.password && !isLoading;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: colorMap.bg.light }}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="animate-float" 
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '300px',
            height: '300px',
            backgroundColor: colorMap.secondary.base,
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            opacity: 0.05,
          }}
        />
      </div>

      <div className={`w-full max-w-md relative z-10 ${mounted ? 'animate-slideInUp' : 'opacity-0'}`}>
        <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(249, 115, 22, 0.1)' }} className="rounded-3xl p-8 lg:p-10 shadow-xl">
          <div className={`mb-8 ${mounted ? 'animate-slideInUp stagger-1' : 'opacity-0'}`}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" 
              style={{ backgroundColor: colorMap.secondary.opacity10 }}>
              <LayoutGrid size={32} style={{ color: colorMap.secondary.base }} />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: colorMap.text.primary }}>
              Admin Panel
            </h1>
            <p style={{ color: colorMap.text.secondary }}>
              Connexion administrateur
            </p>
          </div>

          {error && (
            <div 
              className="mb-6 p-4 rounded-2xl flex items-start gap-3 animate-slideInUp"
              style={{ 
                backgroundColor: `${colorMap.status.error}15`,
                borderLeft: `4px solid ${colorMap.status.error}`
              }}
            >
              <AlertCircle size={20} style={{ color: colorMap.status.error }} className="flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium" style={{ color: colorMap.status.error }}>
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleLogin} className={`space-y-5 ${mounted ? 'animate-slideInUp stagger-2' : 'opacity-0'}`}>
            <div>
              <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
                Email
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Mail 
                    size={20}
                    style={{ 
                      color: focusedField === 'email' ? colorMap.primary.base : colorMap.text.muted,
                      transition: 'color 0.3s'
                    }}
                  />
                </div>
                <input
                  type="email"
                  placeholder="admin@crouville.com"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (error) setError('');
                  }}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300 border-2 bg-white"
                  style={{
                    borderColor: focusedField === 'email' ? colorMap.primary.base : colorMap.border.default
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Lock 
                    size={20}
                    style={{ 
                      color: focusedField === 'password' ? colorMap.primary.base : colorMap.text.muted,
                      transition: 'color 0.3s'
                    }}
                  />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (error) setError('');
                  }}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full pl-12 pr-12 py-3.5 rounded-2xl outline-none transition-all duration-300 border-2 bg-white"
                  style={{
                    borderColor: focusedField === 'password' ? colorMap.primary.base : colorMap.border.default
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff size={20} style={{ color: colorMap.text.muted }} />
                  ) : (
                    <Eye size={20} style={{ color: colorMap.text.muted }} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!canLogin}
              className="w-full py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all duration-300 mt-6"
              style={{
                backgroundColor: canLogin ? colorMap.primary.base : colorMap.bg.disabled,
                color: colorMap.bg.white,
                cursor: canLogin ? 'pointer' : 'not-allowed',
                boxShadow: canLogin ? '0 20px 40px -12px rgba(34, 197, 94, 0.4)' : 'none',
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Connexion...</span>
                </>
              ) : (
                <>
                  <span>Se connecter</span>
                  <LogIn size={20} />
                </>
              )}
            </button>
          </form>

          <p className={`text-center text-sm mt-6 ${mounted ? 'animate-slideInUp stagger-3' : 'opacity-0'}`} 
            style={{ color: colorMap.text.secondary }}>
            Pas encore inscrit ?{' '}
            <button
              onClick={() => setAuthState('register')}
              className="font-bold transition-colors"
              style={{ color: colorMap.primary.base }}
            >
              Créer un compte
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

/* REGISTER */
function AdminRegister({ setAuthState, setIsLoggedIn, setToken, setAdmin }) {
  const [formData, setFormData] = useState({ email: '', password: '', fullName: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('register_admin', {
        p_email: formData.email,
        p_password: formData.password,
        p_full_name: formData.fullName,
      });

      if (rpcError) throw rpcError;
      if (!data.success) {
        throw new Error(data.error || 'Erreur d\'inscription');
      }

      const mockToken = 'supabase_admin_session_' + data.admin.id;

      localStorage.setItem('adminToken', mockToken);
      localStorage.setItem('adminUser', JSON.stringify(data.admin));
      
      setToken(mockToken);
      setAdmin(data.admin);
      setIsLoggedIn(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const canRegister = formData.email && formData.password && formData.fullName && formData.password.length >= 8 && !isLoading;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: colorMap.bg.light }}>
      <div className={`w-full max-w-md relative z-10 ${mounted ? 'animate-slideInUp' : 'opacity-0'}`}>
        <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(249, 115, 22, 0.1)' }} className="rounded-3xl p-8 lg:p-10 shadow-xl">
          <div className={`mb-8 ${mounted ? 'animate-slideInUp stagger-1' : 'opacity-0'}`}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" 
              style={{ backgroundColor: colorMap.secondary.opacity10 }}>
              <User size={32} style={{ color: colorMap.secondary.base }} />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: colorMap.text.primary }}>
              Créer un compte
            </h1>
            <p style={{ color: colorMap.text.secondary }}>
              Inscription administrateur
            </p>
          </div>

          {error && (
            <div 
              className="mb-6 p-4 rounded-2xl flex items-start gap-3 animate-slideInUp"
              style={{ 
                backgroundColor: `${colorMap.status.error}15`,
                borderLeft: `4px solid ${colorMap.status.error}`
              }}
            >
              <AlertCircle size={20} style={{ color: colorMap.status.error }} className="flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium" style={{ color: colorMap.status.error }}>
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleRegister} className={`space-y-5 ${mounted ? 'animate-slideInUp stagger-2' : 'opacity-0'}`}>
            <div>
              <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
                Nom complet
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <User 
                    size={20}
                    style={{ 
                      color: focusedField === 'fullName' ? colorMap.primary.base : colorMap.text.muted,
                      transition: 'color 0.3s'
                    }}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Jean Kouamé"
                  value={formData.fullName}
                  onChange={(e) => {
                    setFormData({ ...formData, fullName: e.target.value });
                    if (error) setError('');
                  }}
                  onFocus={() => setFocusedField('fullName')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300 border-2 bg-white"
                  style={{
                    borderColor: focusedField === 'fullName' ? colorMap.primary.base : colorMap.border.default
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
                Email
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Mail 
                    size={20}
                    style={{ 
                      color: focusedField === 'email' ? colorMap.primary.base : colorMap.text.muted,
                      transition: 'color 0.3s'
                    }}
                  />
                </div>
                <input
                  type="email"
                  placeholder="admin@crouville.com"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (error) setError('');
                  }}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300 border-2 bg-white"
                  style={{
                    borderColor: focusedField === 'email' ? colorMap.primary.base : colorMap.border.default
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
                Mot de passe (min 8 caractères)
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Lock 
                    size={20}
                    style={{ 
                      color: focusedField === 'password' ? colorMap.primary.base : colorMap.text.muted,
                      transition: 'color 0.3s'
                    }}
                  />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (error) setError('');
                  }}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full pl-12 pr-12 py-3.5 rounded-2xl outline-none transition-all duration-300 border-2 bg-white"
                  style={{
                    borderColor: focusedField === 'password' ? colorMap.primary.base : colorMap.border.default
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff size={20} style={{ color: colorMap.text.muted }} />
                  ) : (
                    <Eye size={20} style={{ color: colorMap.text.muted }} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!canRegister}
              className="w-full py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all duration-300 mt-6"
              style={{
                backgroundColor: canRegister ? colorMap.primary.base : colorMap.bg.disabled,
                color: colorMap.bg.white,
                cursor: canRegister ? 'pointer' : 'not-allowed',
                boxShadow: canRegister ? '0 20px 40px -12px rgba(34, 197, 94, 0.4)' : 'none',
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Création...</span>
                </>
              ) : (
                <>
                  <span>Créer un compte</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <p className={`text-center text-sm mt-6 ${mounted ? 'animate-slideInUp stagger-3' : 'opacity-0'}`} 
            style={{ color: colorMap.text.secondary }}>
            Déjà inscrit ?{' '}
            <button
              onClick={() => setAuthState('login')}
              className="font-bold transition-colors"
              style={{ color: colorMap.primary.base }}
            >
              Se connecter
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

/* SIDEBAR */
function Sidebar({ isOpen, setIsOpen, currentPage, setCurrentPage, admin, setIsLoggedIn }) {
  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutGrid },
    { id: 'products', label: 'Produits', icon: ShoppingBag },
    { id: 'sellers', label: 'Vendeurs', icon: Users },
    { id: 'orders', label: 'Consultations', icon: Eye },
    { id: 'analytics', label: 'Statistiques', icon: BarChart3 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ];

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsLoggedIn(false);
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div 
        className={`fixed lg:static top-0 left-0 h-screen w-64 transition-all duration-300 z-50 lg:z-0 flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{ backgroundColor: colorMap.bg.white, borderRight: `1px solid ${colorMap.border.default}` }}
      >
        <div className="p-6 border-b" style={{ borderBottomColor: colorMap.border.default }}>
          <div className="flex items-center justify-between mb-6 lg:mb-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" 
                style={{ backgroundColor: colorMap.secondary.opacity10 }}>
                <ShoppingBag size={20} style={{ color: colorMap.secondary.base }} />
              </div>
              <div>
                <h1 className="font-bold text-sm" style={{ color: colorMap.text.primary }}>CrouVente</h1>
                <p className="text-xs" style={{ color: colorMap.text.muted }}>Admin</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="lg:hidden"
            >
              <X size={20} style={{ color: colorMap.text.secondary }} />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentPage(item.id);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200"
                style={{
                  backgroundColor: isActive ? colorMap.secondary.opacity10 : 'transparent',
                  color: isActive ? colorMap.secondary.base : colorMap.text.secondary,
                }}
              >
                <Icon size={20} />
                <span className="font-medium text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderTopColor: colorMap.border.default }}>
          <div className="flex items-center gap-3 p-3 rounded-xl mb-3" style={{ backgroundColor: colorMap.bg.light }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center" 
              style={{ backgroundColor: colorMap.secondary.opacity10 }}>
              <User size={18} style={{ color: colorMap.secondary.base }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate" style={{ color: colorMap.text.primary }}>
                {admin?.fullName || 'Admin'}
              </p>
              <p className="text-xs truncate" style={{ color: colorMap.text.muted }}>
                {admin?.email || ''}
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all duration-200"
            style={{
              backgroundColor: colorMap.status.error,
              color: colorMap.bg.white,
            }}
          >
            <LogOut size={18} />
            <span className="font-medium text-sm">Déconnexion</span>
          </button>
        </div>
      </div>
    </>
  );
}

/* TOPBAR */
function TopBar({ sidebarOpen, setSidebarOpen, admin }) {
  return (
    <div 
      className="h-16 px-6 flex items-center justify-between border-b"
      style={{ 
        backgroundColor: colorMap.bg.white,
        borderBottomColor: colorMap.border.default 
      }}
    >
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden p-2 rounded-lg transition-colors"
        style={{ color: colorMap.text.secondary }}
      >
        <Menu size={24} />
      </button>

      <div className="flex-1 lg:block hidden" />

      <div className="flex items-center gap-4">
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ backgroundColor: colorMap.bg.light }}>
          <div className="w-2 h-2 rounded-full animate-pulse" 
            style={{ backgroundColor: colorMap.status.success }} 
          />
          <span className="text-sm font-medium" style={{ color: colorMap.text.dark }}>
            En ligne
          </span>
        </div>
      </div>
    </div>
  );
}

/* NOTIFICATIONS PAGE */
function NotificationsPage({ token }) {
  const { notifications, unreadCount, isConnected, setNotifications, setUnreadCount } = useNotificationWebSocket(token);
  const [users, setUsers] = useState([]);
  const [sendingTo, setSendingTo] = useState('');
  const [notifForm, setNotifForm] = useState({ title: '', message: '', type: 'info' });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, phone');
      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Erreur chargement users:', err);
    }
  }, []);

  // Charger les utilisateurs
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const sendNotification = async (e) => {
    e.preventDefault();
    if (!notifForm.title || !notifForm.message) return;

    setSending(true);
    setError('');
    setSuccess('');

    try {
      if (sendingTo === 'broadcast') {
        // Envoi à tous les utilisateurs
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id');
        if (usersError) throw usersError;

        const inserts = (usersData || []).map(u => ({
          user_id: u.id,
          title: notifForm.title,
          message: notifForm.message,
          type: notifForm.type,
          is_viewed: false
        }));

        const { error: insertError } = await supabase
          .from('notifications')
          .insert(inserts);

        if (insertError) throw insertError;
      } else {
        // Envoi à un utilisateur spécifique
        const { error: insertError } = await supabase
          .from('notifications')
          .insert({
            user_id: sendingTo,
            title: notifForm.title,
            message: notifForm.message,
            type: notifForm.type,
            is_viewed: false
          });

        if (insertError) throw insertError;
      }

      setSuccess('Notification envoyée avec succès !');
      setNotifForm({ title: '', message: '', type: 'info' });
      setSendingTo('');

    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_viewed: true, viewed_at: new Date().toISOString() })
        .eq('id', notifId);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(n => n.id === notifId ? { ...n, is_viewed: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Erreur mark as read:', err);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: colorMap.text.primary }}>
            Notifications
          </h1>
          <p style={{ color: colorMap.text.secondary }}>
            Gérez et envoyez des notifications aux utilisateurs
          </p>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 rounded-xl"
          style={{ backgroundColor: isConnected ? colorMap.status.success : colorMap.status.error }}>
          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-sm font-medium text-white">
            {isConnected ? 'Connecté' : 'Déconnecté'}
          </span>
        </div>
      </div>

      {/* Formulaire d'envoi */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: colorMap.bg.white, border: `1px solid ${colorMap.border.default}` }}>
        <h2 className="text-lg font-bold mb-4" style={{ color: colorMap.text.primary }}>
          Envoyer une notification
        </h2>

        {success && (
          <div className="mb-4 p-4 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: `${colorMap.status.success}15`, borderLeft: `4px solid ${colorMap.status.success}` }}>
            <CheckCircle size={20} style={{ color: colorMap.status.success }} />
            <span className="font-medium" style={{ color: colorMap.status.success }}>{success}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: `${colorMap.status.error}15`, borderLeft: `4px solid ${colorMap.status.error}` }}>
            <AlertCircle size={20} style={{ color: colorMap.status.error }} />
            <span className="font-medium" style={{ color: colorMap.status.error }}>{error}</span>
          </div>
        )}

        <form onSubmit={sendNotification} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colorMap.text.dark }}>
              Destinataire
            </label>
            <select
              value={sendingTo}
              onChange={(e) => setSendingTo(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 outline-none transition-all"
              style={{ borderColor: colorMap.border.default }}
              required
            >
              <option value="">Sélectionner un destinataire</option>
              <option value="broadcast">📢 Tous les utilisateurs</option>
              <optgroup label="Utilisateurs">
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name || user.phone} - {user.email || 'Pas d\'email'}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colorMap.text.dark }}>
              Type
            </label>
            <select
              value={notifForm.type}
              onChange={(e) => setNotifForm({ ...notifForm, type: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 outline-none transition-all"
              style={{ borderColor: colorMap.border.default }}
            >
              <option value="info">ℹ️ Information</option>
              <option value="success">✅ Succès</option>
              <option value="warning">⚠️ Avertissement</option>
              <option value="error">❌ Erreur</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colorMap.text.dark }}>
              Titre
            </label>
            <input
              type="text"
              value={notifForm.title}
              onChange={(e) => setNotifForm({ ...notifForm, title: e.target.value })}
              placeholder="Titre de la notification"
              className="w-full px-4 py-3 rounded-xl border-2 outline-none transition-all"
              style={{ borderColor: colorMap.border.default }}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: colorMap.text.dark }}>
              Message
            </label>
            <textarea
              value={notifForm.message}
              onChange={(e) => setNotifForm({ ...notifForm, message: e.target.value })}
              placeholder="Contenu de la notification"
              rows={4}
              className="w-full px-4 py-3 rounded-xl border-2 outline-none transition-all resize-none"
              style={{ borderColor: colorMap.border.default }}
              required
            />
          </div>

          <button
            type="submit"
            disabled={sending || !sendingTo}
            className="w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
            style={{
              backgroundColor: sending || !sendingTo ? colorMap.bg.disabled : colorMap.primary.base,
              color: colorMap.bg.white,
              cursor: sending || !sendingTo ? 'not-allowed' : 'pointer'
            }}
          >
            {sending ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <Send size={20} />
                <span>Envoyer la notification</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Liste des notifications reçues */}
      <div className="rounded-2xl p-6" style={{ backgroundColor: colorMap.bg.white, border: `1px solid ${colorMap.border.default}` }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ color: colorMap.text.primary }}>
            Historique ({notifications.length})
          </h2>
          {unreadCount > 0 && (
            <span className="px-3 py-1 rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: colorMap.status.error }}>
              {unreadCount} non lues
            </span>
          )}
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
          {notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell size={48} style={{ color: colorMap.text.muted, margin: '0 auto' }} className="mb-3" />
              <p style={{ color: colorMap.text.secondary }}>Aucune notification</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className="p-4 rounded-xl transition-all"
                style={{
                  backgroundColor: notif.is_viewed ? colorMap.bg.light : colorMap.secondary.opacity10,
                  border: `1px solid ${notif.is_viewed ? colorMap.border.default : colorMap.secondary.light}`
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-bold mb-1" style={{ color: colorMap.text.primary }}>
                      {notif.title}
                    </h3>
                    <p className="text-sm mb-2" style={{ color: colorMap.text.secondary }}>
                      {notif.message}
                    </p>
                    <p className="text-xs" style={{ color: colorMap.text.muted }}>
                      {new Date(notif.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>

                  {!notif.is_viewed && (
                    <button
                      onClick={() => markAsRead(notif.id)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: colorMap.primary.base }}
                      title="Marquer comme lu"
                    >
                      <CheckCircle size={20} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* PLACEHOLDER PAGES */
function DashboardPage() {
  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colorMap.text.primary }}>
        Tableau de bord
      </h1>
      <p style={{ color: colorMap.text.secondary }}>Contenu du tableau de bord à venir...</p>
    </div>
  );
}

function ProductsPage() {
  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colorMap.text.primary }}>
        Gestion des produits
      </h1>
      <p style={{ color: colorMap.text.secondary }}>Liste des produits à venir...</p>
    </div>
  );
}

function SellersPage() {
  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colorMap.text.primary }}>
        Gestion des vendeurs
      </h1>
      <p style={{ color: colorMap.text.secondary }}>Liste des vendeurs à venir...</p>
    </div>
  );
}

function OrdersPage() {
  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colorMap.text.primary }}>
        Consultations
      </h1>
      <p style={{ color: colorMap.text.secondary }}>Historique des consultations à venir...</p>
    </div>
  );
}

function AnalyticsPage() {
  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colorMap.text.primary }}>
        Statistiques
      </h1>
      <p style={{ color: colorMap.text.secondary }}>Graphiques et statistiques à venir...</p>
    </div>
  );
}

function SettingsPage({ admin }) {
  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colorMap.text.primary }}>
        Paramètres
      </h1>
      <div className="rounded-2xl p-6" style={{ backgroundColor: colorMap.bg.white, border: `1px solid ${colorMap.border.default}` }}>
        <h2 className="font-bold mb-4" style={{ color: colorMap.text.primary }}>
          Informations du compte
        </h2>
        <div className="space-y-3">
          <div>
            <p className="text-sm" style={{ color: colorMap.text.muted }}>Nom</p>
            <p className="font-semibold" style={{ color: colorMap.text.primary }}>{admin?.fullName}</p>
          </div>
          <div>
            <p className="text-sm" style={{ color: colorMap.text.muted }}>Email</p>
            <p className="font-semibold" style={{ color: colorMap.text.primary }}>{admin?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
}